<template>
    <form class="h-full w-full flex flex-col p-8" @submit.prevent="submitForm">
        <div class="grid grid-cols-2 gap-6">

            <div class="flex-shrink-0">
                <div v-if="profilePicturePreview" class="relative">
                    <div @click.stop="cancelUploadedPhoto" class="absolute top-0 left-0 cursor-pointer h-6 w-6 border border-red-600 rounded-full flex flex-col items-center justify-center">
                        <icon-close class="h-4 w-4 text-red-600"></icon-close>
                    </div>
                    <img class="h-48 object-contain" :src="profilePicturePreview" >
                </div>
                <img v-else class="h-48" src="../../assets/images/default/avatar.jpg" >
                <div class="flex justify-start mt-4">
                    <input ref="profilePicture" class="hidden" id="profile-picture" @change="handleProfilePicture" type="file" accept="image/*">
                    <div @click.stop="onPickFile" class="text-twelve flex items-center cursor-pointer">
                        <icon-plus class="h-4 w-4"></icon-plus>
                        <span class="ml-1">Upload Photo*</span>
                    </div>
                </div>
                <transition name="fade" mode="out-in">
                    <p v-if="!profilePicture.isValid" class="h-4 text-xs text-red-600">
                        {{ profilePicture.msg }}
                    </p>
                </transition>
            </div>

            <div class="flex-shrink-0">
                <div v-if="companyLogoPreview" class="relative">
                    <div @click.stop="cancelCompanyLogoPhoto" class="absolute top-0 left-0 cursor-pointer h-6 w-6 border border-red-600 rounded-full flex flex-col items-center justify-center">
                        <icon-close class="h-4 w-4 text-red-600"></icon-close>
                    </div>
                    <img class="h-48 object-contain" :src="companyLogoPreview" >
                </div>
                <img v-else class="h-48" src="../../assets/images/default/logo.png" >
                <div class="flex justify-start mt-4">
                    <input ref="companyLogo" class="hidden" id="company-logo" @change="handleCompanyLogo" type="file" accept="image/*">
                    <div @click.stop="onCompanyLogoPickFile" class="text-twelve flex items-center cursor-pointer">
                        <icon-plus class="h-4 w-4"></icon-plus>
                        <span class="ml-1">Company Logo*</span>
                    </div>
                </div>
                <transition name="fade" mode="out-in">
                    <p v-if="!companyLogo.isValid" class="h-4 text-xs text-red-600">
                        {{ companyLogo.msg }}
                    </p>
                </transition>
            </div>


            <div class="flex flex-col" :class="{ invalid: !name.isValid }">
                <label for="name">Name*</label>
                <input
                    type="text"
                    id="name"
                    v-model.trim="name.val"
                    placeholder=""
                    @blur="clearValidity('name')"
                />
                <transition name="fade" mode="out-in">
                    <p v-if="!name.isValid" class="h-4 text-xs text-red-600">
                        {{ name.msg }}
                    </p>
                </transition>
            </div>
            <div class="flex flex-col" :class="{ invalid: !designation.isValid }">
                <label for="designation">Designation*</label>
                <input
                    type="text"
                    id="designation"
                    v-model.trim="designation.val"
                    placeholder=""
                    @blur="clearValidity('designation')"
                />
                <transition name="fade" mode="out-in">
                    <p v-if="!designation.isValid" class="h-4 text-xs text-red-600">
                        {{ designation.msg }}
                    </p>
                </transition>
            </div>
        </div>
        <div class="mt-6 grid grid-cols-1 gap-6">
            <div class="flex flex-col" :class="{ invalid: !testimonial.isValid }">
                <label for="testimonial">Testimonial*</label>
                <div class="h-32 rounded-lg">
                    <textarea id="testimonial" v-model.trim="testimonial.val" @blur="clearValidity('testimonial')" placeholder="" class="bg-white resize-none w-full h-full outline-none text-sm border p-4 rounded-lg"></textarea>
                </div>
                <transition name="fade" mode="out-in">
                    <p v-if="!testimonial.isValid" class="h-4 text-xs text-red-600">
                        {{ testimonial.msg }}
                    </p>
                </transition>
            </div>
        </div>

        <div class="w-full flex justify-center mt-8">
            <div v-if="isLoading" class="w-36 border-2 font-medium border-ternary text-ternary bg-white rounded-lg px-4 py-2 cursor-pointer">
                <base-spinner-with-text class-list="h-6 w-6 text-ternary capitalize" :text="getLoadingBtnText" text-class-list="text-ternary"></base-spinner-with-text>
            </div>
            <button v-else class="w-36 border-2 font-medium border-ternary text-white hover:text-ternary bg-ternary hover:bg-white rounded-lg px-4 py-2 uppercase text-sm cursor-pointer">{{ getBtnText }}</button>
        </div>
    </form>
</template>

<script>
import gql from 'graphql-tag';
import IconClose from "../../components/icons/IconClose.vue";
import IconPlus from "../../components/icons/IconPlus.vue";
export default {
    name: "testimonial-form",
    emits: ["created", "updated", "close"],
    props: ["mode", "selectedItem"],
    components: {
        IconClose,
        IconPlus,
    },
    data() {
        return {
            name: {
                val: "",
                isValid: true,
                msg: "",
            },
            designation: {
                val: "",
                isValid: true,
                msg: "",
            },
            testimonial: {
                val: "",
                isValid: true,
                msg: "",
            },
            profilePicture: {
                val: null,
                isValid: true,
                msg: "",
            },
            profilePicturePreview: null,
            companyLogo: {
                val: null,
                isValid: true,
                msg: "",
            },
            companyLogoPreview: null,
            formIsValid: true,
            isLoading: false,
            error: null,
        };
    },
    created() {
        if (this.mode == 'edit') {
            this.setFormData();
        }
    },
    computed: {
        getBtnText() {
            if (this.mode == 'create') {
                return "Create";
            } else {
                return "Update";
            }
        },
        getLoadingBtnText() {
            if (this.mode == 'create') {
                return "Creating...";
            } else {
                return "Updating...";
            }
        },
    },
    methods: {
        clearValidity(input) {
            this[input].isValid = true;
            this[input].msg = "";
        },
        validateForm() {
            this.formIsValid = true;
            
            if (this.mode == 'create') {
                if (this.profilePicture.val === null) {
                    this.profilePicture.isValid = false;
                    this.profilePicture.msg = "You must upload a photo.";
                    this.formIsValid = false;
                }
                if (this.companyLogo.val === null) {
                    this.companyLogo.isValid = false;
                    this.companyLogo.msg = "You must upload company logo.";
                    this.formIsValid = false;
                }
            }
            if (this.name.val === "") {
                this.name.isValid = false;
                this.name.msg = "Name must not be empty.";
                this.formIsValid = false;
            }
            if (this.designation.val === "") {
                this.designation.isValid = false;
                this.designation.msg = "Designation must not be empty.";
                this.formIsValid = false;
            }
            if (this.testimonial.val === "") {
                this.testimonial.isValid = false;
                this.testimonial.msg = "Testimonial must not be empty.";
                this.formIsValid = false;
            }
            
        },
        async submitForm() {
            this.validateForm();

            if (!this.formIsValid) {
                return;
            }

            const formData = {
                profile_picture: this.profilePicture.val,
                thumbnail: this.mode == 'create' ? '' : this.selectedItem.thumbnail,
                logo: this.companyLogo.val,
                company_logo: this.mode == 'create' ? '' : this.selectedItem.company_logo,
                name: this.name.val,
                designation: this.designation.val,
                testimonial: this.testimonial.val,
            };

            const CREATE_MUTATION = gql`
                mutation CreateTestimonial($input: CreateTestimonialInput!) {
                    createTestimonial(input: $input) {
                        error
                        message
                    }
                }
            `;

            const UPDATE_MUTATION = gql`
                mutation UpdateTestimonial($token: String!, $input: CreateTestimonialInput!) {
                    updateTestimonial(token: $token, input: $input) {
                        error
                        message
                    }
                }
            `;

            this.isLoading = true;
            let error_msg = null;

            try {
                await this.$apollo.mutate({
                    mutation: this.mode == 'create' ? CREATE_MUTATION : UPDATE_MUTATION,
                    variables: {
                        token: this.mode == 'edit' ? this.selectedItem.token : null,
                        input: formData
                    }
                }).then((data) => {
                    if (this.mode == 'create') {
                        if (data.data.createTestimonial.error) {
                            error_msg = data.data.createTestimonial.message;
                        }
                    } else {
                        if (data.data.updateTestimonial.error) {
                            error_msg = data.data.updateTestimonial.message;
                        }
                    }
                }).catch((error) => {
                    error_msg = error;
                })

            } catch (error) {
                error_msg = error.message || "Failed to authenticate, try later.";
            }

            if (error_msg === null) {
                if (this.mode == 'create') {
                    this.$emit('created');
                } else {
                    this.$emit('updated');
                }
            } else {
                this.$swal.fire({
                    icon: 'error',
                    title: 'Oops...',
                    text: error_msg,
                }).then((result) => {
                    if (result.value || result.dismiss) {
                        this.$emit('close');
                    }
                })
            }

            this.isLoading = false;

        },
        setFormData() {
            this.profilePicturePreview = process.env.VUE_APP_PUBLIC_URL + this.selectedItem.thumbnail;
            this.companyLogoPreview = process.env.VUE_APP_PUBLIC_URL + this.selectedItem.company_logo;
            this.name.val = this.selectedItem.name;
            this.designation.val = this.selectedItem.designation;
            this.testimonial.val = this.selectedItem.testimonial;
        },
        onPickFile() {
            this.$refs.profilePicture.click();
        },
        onCompanyLogoPickFile() {
            this.$refs.companyLogo.click();
        },
        handleProfilePicture(event) {
            this.profilePicture.isValid = true;
            this.profilePicture.msg = "";
            const file = event.target.files[0];
            this.profilePicture.val = file;
            if (file) {
                this.profilePicturePreview = URL.createObjectURL(file);
            }
        },
        handleCompanyLogo(event) {
            this.companyLogo.isValid = true;
            this.companyLogo.msg = "";
            const file = event.target.files[0];
            this.companyLogo.val = file;
            if (file) {
                this.companyLogoPreview = URL.createObjectURL(file);
            }
        },
        cancelUploadedPhoto() {
            this.profilePicture.val = null;
            this.profilePicturePreview = null;
        },
        cancelCompanyLogoPhoto() {
            this.companyLogo.val = null;
            this.companyLogoPreview = null;
        },
    },
}
</script>

<style lang="scss" scoped>
label {
  @apply mb-1 block text-sm;
}
input[type="text"],
input[type="email"],
input[type="number"],
input[type="password"],
input[type="date"],
select {
  @apply block w-full border rounded-lg py-2 px-2;
}
input[type="text"]:focus,
input[type="email"]:focus,
input[type="number"]:focus,
input[type="password"]:focus,
input[type="date"]:focus,
select:focus {
  outline: none;
  border-color: #394867;
  box-shadow: 0 0 0 2px #394867;
}
.invalid label {
  @apply text-red-600;
}
.invalid input,
.invalid select {
  border: 1px solid red;
}
input[type="checkbox"] {
  @apply w-auto;
}
.fade-enter-active,
.fade-leave-active {
  transition: all 0.5s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  height: 0px;
}
</style>